import React, { useState, useMemo } from 'react';
import { MdClose } from 'react-icons/md';
import IsometricBuilding from '../../components/IsometricBuilding';

const FLOOR_COLORS = [
  '#ef4444','#f97316','#eab308','#22c55e','#14b8a6',
  '#3b82f6','#8b5cf6','#ec4899','#06b6d4','#84cc16',
  '#f59e0b','#10b981','#6366f1','#e11d48','#0ea5e9',
];

export default function Building3DView({ onClose, images = [] }) {
  const [floorCount, setFloorCount] = useState(5);
  const [selectedFloor, setSelectedFloor] = useState(null);

  // Reset selection if it's out of range after changing floor count
  const activeFloor = selectedFloor !== null && selectedFloor < floorCount ? selectedFloor : null;

  const floorImages = useMemo(() => {
    if (!images.length) return {};
    const map = {};
    for (let i = 0; i < floorCount; i++) {
      map[i] = images.filter((_, idx) => idx % floorCount === i);
    }
    return map;
  }, [images, floorCount]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div style={{
        background: '#fff', borderRadius: '20px',
        width: '100%', maxWidth: '480px',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px 12px',
          borderBottom: '1px solid #f0f0f0',
        }}>
          <span style={{ fontWeight: 700, fontSize: '16px', color: '#111' }}>Building View</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <MdClose style={{ fontSize: '22px', color: '#555' }} />
          </button>
        </div>

        {/* Floor selector */}
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label style={{ fontSize: '13px', color: '#555', whiteSpace: 'nowrap' }}>Floors</label>
          <select
            value={floorCount}
            onChange={e => { setFloorCount(Number(e.target.value)); setSelectedFloor(null); }}
            style={{
              border: '1.5px solid #dde', borderRadius: '8px',
              padding: '6px 10px', fontSize: '14px', fontWeight: 600,
              color: 'var(--color-primary)', background: '#f0f4ff', cursor: 'pointer',
            }}
          >
            {Array.from({ length: 14 }, (_, i) => i + 2).map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          {activeFloor !== null && (
            <span style={{ fontSize: '13px', color: 'var(--color-primary)', fontWeight: 600 }}>
              Floor {activeFloor + 1} selected
            </span>
          )}
        </div>

        {/* 3D Building SVG */}
        <div style={{ padding: '0 20px', display: 'flex', justifyContent: 'center' }}>
          <IsometricBuilding
            floorCount={floorCount}
            selectedFloor={activeFloor}
            onFloorClick={setSelectedFloor}
          />
        </div>

        {/* Floor info panel */}
        <div style={{ padding: '12px 20px 20px' }}>
          {activeFloor === null ? (
            <p style={{ textAlign: 'center', color: '#999', fontSize: '13px', margin: 0 }}>
              Tap a floor to view details
            </p>
          ) : (
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: '12px',
              }}>
                <div style={{
                  width: '12px', height: '12px', borderRadius: '3px',
                  background: FLOOR_COLORS[activeFloor % FLOOR_COLORS.length],
                }} />
                <span style={{ fontWeight: 700, fontSize: '14px', color: '#111' }}>
                  Floor {activeFloor + 1}
                </span>
              </div>
              {floorImages[activeFloor]?.length > 0 ? (
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                  {floorImages[activeFloor].map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt={`Floor ${activeFloor + 1}`}
                      style={{
                        width: '110px', height: '80px',
                        objectFit: 'cover', borderRadius: '10px', flexShrink: 0,
                      }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ))}
                </div>
              ) : (
                <div style={{
                  background: '#f5f7ff', borderRadius: '12px',
                  padding: '20px', textAlign: 'center',
                  color: '#aab', fontSize: '13px',
                }}>
                  No images assigned to this floor yet
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
